import { useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import api from "../../../services/newApi/api";
import { useAuth } from "../context/useAuth";
import { homePathFor } from "../access/homePathFor";
import { toast } from "react-toastify";
import { isAxiosError } from "axios";
import { getApiErrorStatus } from "../../../utils/apiError";
import { sanitizeErrorMessage } from "../utils/authErrorHandler";

interface SwitchUserResult {
  success: boolean;
  error?: string;
}

// P20EE-196: the server refuses an impersonation with "Forbidden: você não tem permissão para personificar usuários"
// (is-impersonator.middleware), and the toast showed it raw, English prefix included (dev, 2026-10-23).
function switchErrorMessage(error: unknown, fallback: string): string {
  if (getApiErrorStatus(error) === 403) return "Operação não permitida para seu perfil.";
  const message = isAxiosError<{ message?: unknown }>(error) ? error.response?.data?.message : undefined;
  return typeof message === "string" && message.trim() ? sanitizeErrorMessage(message) : fallback;
}

export function useSwitchUser() {
  const [isLoading, setIsLoading] = useState(false);
  const { user, applyDirectSession, setUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Adota a sessão retornada no corpo (token do alvo/admin) SEM reload e navega
  // client-side. O reload descartava o access token (só vive em memória) e a
  // sessão era reconstruída como o ADMIN → role_id 1 / 243 hospitais. Adotar em
  // memória mantém o token correto; o ActiveHospitalProvider reage à troca de
  // auth.user e re-hidrata o hospital; o banner lê o impersonatorId do JWT.
  const adoptAndGo = useCallback(
    async (data: any) => {
      applyDirectSession(data);
      // ponytail: limpa TODO o cache — a identidade muda por completo, qualquer
      // query em cache está escopada ao ator anterior.
      queryClient.clear();

      // O corpo de /auth/switch-user e /auth/switch-back traz a LINHA CRUA do usuário
      // (switch-user.usecase.ts: `const { password, ...sanitizedUser } = target`), sem `roles` e
      // sem `permissoes` — que é justamente o que a home composta usa para decidir bloco. Só
      // /auth/validation monta o payload enriquecido (união do account-link + permissões
      // escopadas ao hospital). Sem esta hidratação, "ver como" mostrava uma capacidade menor
      // que a real do alvo, e o switch-back devolvia o admin sem os blocos dele até um F5.
      try {
        const validated = await api.post("/auth/validation");
        if (validated.data?.user) setUser(validated.data.user);
      } catch {
        // A sessão já foi adotada; sem o enriquecimento a tela mostra menos, mas não quebra.
      }

      // Destino pela regra única: `/inicio` incondicional pousava o médico
      // interno na home composta vazia, que por ADR não é dele (G7).
      navigate(homePathFor(data?.user));
    },
    [applyDirectSession, setUser, queryClient, navigate]
  );

  const switchUser = useCallback(
    async (targetUserId: number): Promise<SwitchUserResult> => {
      if (user?.role_id !== 1) {
        toast.error("Apenas administradores podem trocar de usuário.");
        return { success: false, error: "Permissão negada" };
      }

      setIsLoading(true);
      try {
        // Admin impersonation endpoint. Backend deriva role/permissões do alvo
        // e retorna o access token do alvo no corpo da resposta.
        const response = await api.post("/auth/switch-user", { userId: targetUserId });

        if (response.data.accessToken) {
          toast.success("Troca de usuário realizada com sucesso.");
          await adoptAndGo(response.data);
          return { success: true };
        }

        return { success: false, error: "Resposta inesperada do servidor" };
      } catch (error: any) {
        const message = switchErrorMessage(error, "Erro ao trocar de usuário");
        toast.error(message);
        return { success: false, error: message };
      } finally {
        setIsLoading(false);
      }
    },
    [user?.role_id, adoptAndGo]
  );

  const switchBack = useCallback(async (): Promise<SwitchUserResult> => {
    setIsLoading(true);
    try {
      const response = await api.post("/auth/switch-back");

      if (response.data.accessToken) {
        toast.success("Sessão original restaurada.");
        await adoptAndGo(response.data);
        return { success: true };
      }

      return { success: false, error: "Resposta inesperada do servidor" };
    } catch (error: any) {
      const message = switchErrorMessage(error, "Erro ao restaurar sessão");
      toast.error(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  }, [adoptAndGo]);

  return {
    isLoading,
    switchUser,
    switchBack,
  };
}
